import { baseApi } from "../baseApi";
import { pick } from "../util";
import type {
  Factory,
  CreateFactoryInput,
  UpdateFactoryInput,
} from "../types";

const FACTORY_FIELDS = `
  id
  organizationId
  name
  code
  email
  phone
  gstin
  contactPerson
  addressLine1
  addressLine2
  city
  state
  country
  postalCode
  leadTimeDays
  status
  createdAt
  updatedAt
`;

export const factoryApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getFactories: builder.query<Factory[], string | void>({
      query: (organizationId) => ({
        body: {
          query: `
            query Factories($organizationId: String) {
              factories(organizationId: $organizationId) {
                ${FACTORY_FIELDS}
              }
            }
          `,
          variables: { organizationId: organizationId ?? null },
        },
      }),

      transformResponse: (res: unknown) => pick<Factory[]>(res, "factories"),

      providesTags: (result) =>
        result
          ? [
              ...result.map((f) => ({ type: "Factory" as const, id: f.id })),
              { type: "Factory" as const, id: "LIST" },
            ]
          : [{ type: "Factory" as const, id: "LIST" }],
    }),

    getFactory: builder.query<Factory, string>({
      query: (id) => ({
        body: {
          query: `
            query Factory($id: String!) {
              factory(id: $id) {
                ${FACTORY_FIELDS}
              }
            }
          `,
          variables: { id },
        },
      }),

      transformResponse: (res: unknown) => pick<Factory>(res, "factory"),

      providesTags: (_result, _err, id) => [{ type: "Factory", id }],
    }),

    createFactory: builder.mutation<Factory, CreateFactoryInput>({
      query: (input) => ({
        body: {
          query: `
            mutation CreateFactory($input: CreateFactoryInput!) {
              createFactory(input: $input) {
                ${FACTORY_FIELDS}
              }
            }
          `,
          variables: { input },
        },
      }),

      transformResponse: (res: unknown) => pick<Factory>(res, "createFactory"),

      invalidatesTags: [{ type: "Factory", id: "LIST" }],
    }),

    updateFactory: builder.mutation<
      Factory,
      { id: string; input: UpdateFactoryInput }
    >({
      query: ({ id, input }) => ({
        body: {
          query: `
            mutation UpdateFactory($id: String!, $input: UpdateFactoryInput!) {
              updateFactory(id: $id, input: $input) {
                ${FACTORY_FIELDS}
              }
            }
          `,
          variables: { id, input },
        },
      }),

      transformResponse: (res: unknown) => pick<Factory>(res, "updateFactory"),

      invalidatesTags: (_result, _err, { id }) => [
        { type: "Factory", id },
        { type: "Factory", id: "LIST" },
      ],
    }),

    deleteFactory: builder.mutation<boolean, string>({
      query: (id) => ({
        body: {
          query: `
            mutation DeleteFactory($id: String!) {
              deleteFactory(id: $id)
            }
          `,
          variables: { id },
        },
      }),

      transformResponse: (res: unknown) => pick<boolean>(res, "deleteFactory"),

      invalidatesTags: (_result, _err, id) => [
        { type: "Factory", id },
        { type: "Factory", id: "LIST" },
      ],
    }),
  }),
});

export const {
  useGetFactoriesQuery,
  useLazyGetFactoriesQuery,
  useGetFactoryQuery,
  useLazyGetFactoryQuery,
  useCreateFactoryMutation,
  useUpdateFactoryMutation,
  useDeleteFactoryMutation,
} = factoryApi;
